/* eslint-disable import/no-cycle */
import { RouteConfig } from 'vue-router';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { IRouteConfigMeta, IRouteConfigAuthMeta, ERouteMetaPermissionMode } from '@/shared';
import { hideFromNavChildClient } from '@/shared/helpers/hide-nav-from-child-client';

const fpnPermission = {
  key: 'floorplan_navigator',
  keyType: ModelType.building,
  mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
};

const auth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ keyType: ModelType.account, key: 'superuser' }, fpnPermission ],
};

const fpnAuth = {
  required: true,
  permissions: [fpnPermission],
};

/** Also a client facing screen */
const floorplanNavigatorList: RouteConfig = {
  path: '/floorplan-navigator',
  name: 'FloorplanNavigatorList',
  component: () => import('@/views/floorplan-navigator/Index.vue'),
  meta: {
    title: 'Floorplan Navigator',
    icon: 'house-blank',
    dynamicHideFromNav: () => app()?.$rsGlobals.$hasClientSettingValue('fpn', '1'),
    parent: null,
    active: false,
    noPagePadding: true,
    auth: fpnAuth,
  },
};

/** Also a client facing screen */
const floorplanNavigatorNonSuperUserDetails: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/details',
  name: 'FloorplanNavigatorNonSuperUserDetails',
  component: () => import('@/views/floorplan-navigator/single/Units.vue'),
  meta: {
    title: 'Floorplan Navigator Details',
    icon: 'house-blank',
    dynamicTitle: () => 'Floorplan Navigator Details',
    dynamicHideFromNav: () => app()?.$rsGlobals.$userHasAccess('superuser'),
    parent: floorplanNavigatorList,
    active: false,
    noPagePadding: true,
    footerBar: {
      hasTranslations: false,
      enabled: true,
      hasSave: true,
    },
    auth: fpnAuth,
  },
};

const floorplanDetails: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/internal-details',
  name: 'FloorplanNavigatorDetails',
  component: () => import('@/views/floorplan-navigator/single/Details.vue'),
  meta: {
    auth,
    title: 'Floorplan Navigator Details',
    icon: 'house-blank',
    dynamicTitle: () => 'Floorplan Navigator Details',
    parent: floorplanNavigatorList,
    active: false,
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const floorplanPointsOfInterest: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/points-of-interest',
  name: 'FloorplanNavigatorPointsOfInterest',
  component: () => import('@/views/floorplan-navigator/single/PointsOfInterest.vue'),
  meta: {
    auth,
    title: 'Floorplan Navigator Points of Interest',
    icon: 'house-blank',
    dynamicTitle: () => 'Floorplan Navigator Points of Interest',
    parent: floorplanNavigatorList,
    active: false,
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslationsBtn: () => true,
    },
  },
};

const floorplanUnits: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/units',
  name: 'FloorplanNavigatorUnits',
  component: () => import('@/views/floorplan-navigator/single/Units.vue'),
  meta: {
    auth,
    title: 'Floorplan Navigator Units',
    icon: 'house-blank',
    dynamicTitle: () => 'Floorplan Navigator Units',
    parent: floorplanNavigatorList,
    active: false,
    noPagePadding: true,
    footerBar: {
      hasTranslations: false,
      enabled: true,
      hasSave: true,
    },
  },
};

const floorplanSettings: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/settings',
  name: 'FloorplanNavigatorSettings',
  component: () => import('@/views/floorplan-navigator/single/fpn-settings/Index.vue'),
  meta: {
    auth,
    title: 'Floorplan Navigator Settings',
    icon: 'house-blank',
    dynamicTitle: () => 'Floorplan Navigator Settings',
    parent: floorplanNavigatorList,
    active: false,
    noPagePadding: true,
    footerBar: {
      hasTranslations: true,
      enabled: true,
      hasSave: true,
      hasTranslationsBtn: () => true,
    },
  },
};

const selfServe: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/self-serve',
  name: 'FPNSelfServe',
  component: () => import('@/views/floorplan-navigator/self-serve/Index.vue'),
  meta: {
    parent: null,
    auth,
    title: 'Self Serve',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'house-blank',
  },
};

const selfServeImport: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/self-serve/import',
  name: 'FPNSelfServeImport',
  component: () => import('@/views/floorplan-navigator/self-serve/Import.vue'),
  meta: {
    parent: null,
    auth,
    title: 'Self Serve Import',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'house-blank',
  },
};

const selfServeUpdateExterior: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/self-serve/update-exterior',
  name: 'FPNSelfServeUpdateExterior',
  component: () => import('@/views/floorplan-navigator/self-serve/UpdateExterior.vue'),
  meta: {
    parent: null,
    auth,
    title: 'Self Serve Update Exterior',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'house-blank',
  },
};

const selfServeUpdateFloors: RouteConfig = {
  path: '/floorplan-navigator/:fpnInstanceId/self-serve/update-floors',
  name: 'FPNSelfServeUpdateFloors',
  component: () => import('@/views/floorplan-navigator/self-serve/UpdateFloors.vue'),
  meta: {
    parent: null,
    auth,
    title: 'Self Serve Update Floors',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'house-blank',
  },
};

const templates: RouteConfig = {
  path: '/floorplan-navigator/templates',
  name: 'FPNTemplates',
  component: () => import('@/views/floorplan-navigator/Templates.vue'),
  meta: {
    parent: null,
    auth,
    title: 'Templates',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'house-blank',
  },
};

const templateDetails: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/details',
  name: 'FPNTemplateDetails',
  component: () => import('@/views/floorplan-navigator/templates/Index.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'Template Details',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};
const floorplanNavigatorSettings: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/settings',
  name: 'FPNSettings',
  component: () => import('@/views/floorplan-navigator/templates/fpn-settings/Index.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'FPN Settings',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
      hasTranslationsBtn: () => true,
    },
  },
};

const listingCardFields: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/listing-card-fields',
  name: 'FPNTemplateListingCardFields',
  component: () => import('@/views/floorplan-navigator/templates/fpn-builder/ListingCardFields.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'Listing Card Fields',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

const filters: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/filters',
  name: 'FPNTemplateFilters',
  component: () => import('@/views/floorplan-navigator/templates/fpn-builder/Filters.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'Filters',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

const modalSettings: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/modal-settings',
  name: 'FPNTemplateModalSettings',
  component: () => import('@/views/floorplan-navigator/templates/fpn-builder/ModalSettings.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'Modal Settings',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

const modalButtons: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/modal-buttons',
  name: 'FPNTemplateModalButtons',
  component: () => import('@/views/floorplan-navigator/templates/fpn-builder/ModalButtons.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'Modal Buttons',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

const modalMediaButtons: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/modal-media-buttons',
  name: 'FPNTemplateModalMediaButtons',
  component: () => import('@/views/floorplan-navigator/templates/fpn-builder/ModalMediaButtons.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'Modal Media Buttons',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

const formSettings: RouteConfig = {
  path: '/floorplan-navigator/templates/:templateId/form-settings',
  name: 'FPNTemplateFormSettings',
  component: () => import('@/views/floorplan-navigator/templates/fpn-builder/FormSettings.vue'),
  meta: {
    parent: templates,
    auth,
    title: 'FPN Form Settings',
    dynamicHideFromNav: hideFromNavChildClient,
    noPagePadding: true,
    active: false,
    icon: 'table-layout',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

export default [
  floorplanNavigatorList,
  floorplanNavigatorNonSuperUserDetails,
  floorplanDetails,
  floorplanPointsOfInterest,
  floorplanUnits,
  floorplanSettings,
  templateDetails,
  floorplanNavigatorSettings,
  listingCardFields,
  filters,
  modalSettings,
  modalButtons,
  modalMediaButtons,
  formSettings,
  templates,
  selfServe,
  selfServeImport,
  selfServeUpdateExterior,
  selfServeUpdateFloors,
] as Array<RouteConfig>;
